import type { ApolloClient } from "@apollo/client";
import { v4 as uuidv4 } from "uuid";
import { gql } from "__generated__/gql";
import entityContextMarkdownSchema from "utils/schemas/entityContext";
import SignedPutUrlFileUpload from "utils/files/uploads/SignedPutUrlFileUpload";

export const ENTITY_CONTEXT_INLINE_MAX_BYTES = 6144;
const ENTITY_CONTEXT_UPLOADS_PATH = "entity-context/";

gql(/* GraphQL */ `
  fragment UpdateEntityContext on Mutation {
    updateOrCreateEntityContext(input: $entityContextInput)
      @include(if: $shouldSaveEntityContext) {
      entityContext {
        id
        markdown
      }
    }
  }
`);

gql(/* GraphQL */ `
  fragment DeleteEntityContext on Mutation {
    deleteEntityContext(input: { id: $entityContextId })
      @include(if: $isEntityContextDeleted) {
      entityContext {
        id
      }
    }
  }
`);

type UpdateEntityContextVariables = {
  entityContextId: UUID;
  isEntityContextDeleted: boolean;
  shouldSaveEntityContext: boolean;
  entityContextInput: {
    entityContextId: UUID;
    markdown?: string;
    markdownUploadKey?: string;
  };
  relatedEntityContextId: UUID | null;
};

function getMarkdownFile(markdown: string, entityContextId: UUID) {
  return new File([markdown], `${entityContextId}.md`, {
    type: "text/markdown;charset=utf-8",
  });
}

function getMarkdownByteLength(markdown: string) {
  return new Blob([markdown]).size;
}

export default async function getUpdateEntityContextVariables(
  apolloClient: ApolloClient<object>,
  entityContextId: UUID | null | undefined,
  markdown: string,
): Promise<UpdateEntityContextVariables> {
  const id = entityContextId || uuidv4();
  const normalizedMarkdown =
    await entityContextMarkdownSchema.validate(markdown);

  const result: UpdateEntityContextVariables = {
    entityContextId: id,
    isEntityContextDeleted: false,
    shouldSaveEntityContext: false,
    entityContextInput: { entityContextId: id, markdown: normalizedMarkdown },
    relatedEntityContextId: null,
  };

  if (!normalizedMarkdown) {
    return {
      ...result,
      isEntityContextDeleted: !!entityContextId,
      relatedEntityContextId: null,
    };
  }

  if (
    getMarkdownByteLength(normalizedMarkdown) <= ENTITY_CONTEXT_INLINE_MAX_BYTES
  ) {
    return {
      ...result,
      shouldSaveEntityContext: true,
      entityContextInput: {
        entityContextId: id,
        markdown: normalizedMarkdown,
      },
      relatedEntityContextId: id,
    };
  }

  const upload = new SignedPutUrlFileUpload({
    apolloClient,
    file: getMarkdownFile(normalizedMarkdown, id),
    filePath: ENTITY_CONTEXT_UPLOADS_PATH,
  });

  await upload.startUploadToPath();

  if (!upload.signedUrlData?.key) {
    throw new Error("Expected markdown upload key after upload.");
  }

  return {
    ...result,
    shouldSaveEntityContext: true,
    entityContextInput: {
      entityContextId: id,
      markdownUploadKey: upload.signedUrlData.key,
    },
    relatedEntityContextId: id,
  };
}
